<template>
  <div
    v-if="!hbtFlowEnabled"
    class="p-6 rounded border border-black-30 sm:p-6 booking-widget resort-booking-widget"
  >
    <div>
      <div>
        <h4 class="fs-20 font-medium flex-1 mb-2">
          Your stay at {{ supplier?.name }}
        </h4>
        <div class="mb-4">Enter your details to create your reservation.</div>
      </div>
      <div class="tripadvisor-sec -mt-6 mb-6 hidden">
        <slot name="tripadvisor" />
      </div>

      <tooltip
        name="ttDates"
        ref="ttDates"
        @show="handleShow"
        @hide="handleHide"
      >
        <template v-slot:trigger="{ toggle, isShowing }">
          <div class="flex items-center relative">
            <svg
              width="24"
              height="24"
              class="flex-none booking-widget-input-icon--left"
            >
              <use xlink:href="/img/sprite.svg#ic_calendar"></use>
            </svg>
            <input
              type="text"
              tabindex="100"
              readonly
              :placeholder="
                supplier.type === 'custom'
                  ? 'Add travel dates'
                  : 'Add travel date'
              "
              :value="dateDisplay"
              class="cursor-pointer rounded input-tall w-full border border-black-30 booking-widget-input"
              :class="{ 'force-focus': isShowing }"
              @click="isMobile() ? openMobileBookingWidget(1) : toggle()"
            />
          </div>
        </template>

        <template v-slot:default="{ toggle }">
          <div
            class="booking-widget-datepicker-container px-5 pt-6 pb-6 bg-white rounded mt-2 md:rounded-none mdsm:p-0"
          >
            <date-picker
              v-if="supplier.type === 'package'"
              v-model="formData.date"
              @close="toggle"
            />
            <date-range-picker
              v-else-if="supplier.type === 'custom'"
              v-model="formData.dates"
              @close="toggle"
            />
          </div>
        </template>
      </tooltip>

      <div class="my-2">
        <responsive>
          <template #lg>
            <guest-picker ref="ttGuests"></guest-picker>
          </template>
          <template #mdsm>
            <div>
              <input
                type="text"
                tabindex="101"
                readonly
                placeholder="Add guests"
                :value="guestsDisplay"
                class="cursor-pointer rounded input-tall w-full border border-black-30 booking-widget-input mdsm:pl-4"
                @click="isMobile() ? openMobileBookingWidget(2) : toggle()"
              />
            </div>
          </template>
        </responsive>
      </div>

      <div class="my-2" v-if="supplier.type === 'package'">
        <responsive>
          <template #lg>
            <package-picker
              ref="ttPackages"
              :options="supplier.resortPackages"
            />
          </template>
          <template #mdsm>
            <div>
              <input
                type="text"
                tabindex="102"
                readonly
                placeholder="Select package length"
                :value="packageDisplay"
                class="cursor-pointer rounded input-tall w-full border border-black-30 booking-widget-input mdsm:pl-4"
                @click="isMobile() ? openMobileBookingWidget(3) : toggle()"
              />
            </div>
          </template>
        </responsive>
      </div>
    </div>

    <div class="flex flex-col justify-between mt-4 mdsm:w-full">
      <button
        type="button"
        tabindex="103"
        class="booking-widget-enquire btn btn-primary font-medium input-tall mdsm:w-full"
        @click="handleSubmitEnquire"
      >
        Create your package
      </button>
    </div>

    <div class="mb-6">
      <hr class="separator-slim my-4" />
      <div v-if="supplierDetailInfo" class="mb-4">
        Or let our {{ getCountryName }} travel expert create the perfect trip
        for you.
      </div>
      <ul class="list-disc ml-5">
        <li>Local knowledge and practical travel advice</li>
        <li>Personalised packages with accommodation, diving, and transfers</li>
        <li>Simple booking with clear pricing and secure payments</li>
      </ul>
    </div>

    <trengo-link>
      <template v-slot="{ openChat }">
        <div class="mt-4 mb-4 md:text-center">
          <button
            type="button"
            tabindex="103"
            class="btn btn-primary btn-outlined input-tall w-full"
            @click="handleChat(openChat)"
          >
            Chat with a travel expert
          </button>
        </div>
      </template>
    </trengo-link>

    <responsive>
      <template #mdsm>
        <mobile-booking-widget
          v-model="formData"
          ref="mobileBookingWidget"
          class="mdsm:visible lg:hidden"
        >
        </mobile-booking-widget>
      </template>
    </responsive>
  </div>
</template>

<script>
import {
  getBookingDateDisplay,
  getBookingGuestsDisplay,
  getSelectedCurrency,
} from "../../helpers";
import { mapGetters } from "vuex";
import get from "lodash/get";
import Responsive from "../core/Responsive";
import Tooltip from "../core/Tooltip";
import DatePicker from "../core/DatePicker";
import DateRangePicker from "../core/DateRangePicker";
import GuestPicker from "../core/Booking/GuestPicker";
import PackagePicker from "../core/Booking/PackagePicker";
import MobileBookingWidget from "./MobileBookingWidget";
import TrengoLink from "../core/TrengoLink";
import CurrencyContext from "../core/CurrencyContext";
import { fetchFromURL } from "../../helpers/topNavHelpers";

const userSearchSettings = fetchFromURL();

export default {
  components: {
    Tooltip,
    GuestPicker,
    PackagePicker,
    DatePicker,
    DateRangePicker,
    MobileBookingWidget,
    Responsive,
    CurrencyContext,
    TrengoLink,
  },
  props: ["resortId", "price", "priceNight"],
  data() {
    const formData = {
      date: null,
      dates: [null, null],
      guests: "0", // string type because we can have value like "10+"
      package: "0", // string type
    };

    // Try getting values from user search settings
    if (
      userSearchSettings.formData.dates[0] &&
      moment(userSearchSettings.formData.dates[0]).isValid()
    ) {
      formData.date = userSearchSettings.formData.dates[0];
    }
    if (
      userSearchSettings.formData.dates[1] &&
      moment(userSearchSettings.formData.dates[1]).isValid()
    ) {
      formData.dates = userSearchSettings.formData.dates;
    }
    if (userSearchSettings.formData.guests !== "0") {
      formData.guests = userSearchSettings.formData.guests;
    }

    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has("from")) {
      formData.dates[0] = moment(searchParams.get("from"), "YYYY-MM-DD", true);
    }
    if (searchParams.has("to")) {
      formData.dates[1] = moment(searchParams.get("to"), "YYYY-MM-DD", true);
    }
    if (searchParams.has("guests")) {
      formData.guests = searchParams.get("guests");
    }

    return {
      formData,
      supplier: {
        name: "",
        resortPackages: [],
        type: "custom",
        availableDays: null,
      },
      supplierDetailInfo: null,
      priceBasis: null,
    };
  },
  provide() {
    return {
      formData: this.formData,
      supplier: this.supplier,
    };
  },
  computed: {
    ...mapGetters(["hbtFlowEnabled"]),
    dateDisplay() {
      if (this.supplier.type === "package") {
        return getBookingDateDisplay(this.formData.date).join(" — ");
      } else if (this.supplier.type === "custom") {
        return getBookingDateDisplay(
          this.formData.dates[0],
          this.formData.dates[1]
        ).join(" — ");
      }
    },
    guestsDisplay() {
      return getBookingGuestsDisplay(this.formData.guests);
    },
    packageDisplay() {
      if (this.formData.package === "0") {
        return "";
      }

      const foundPackage = this.supplier.resortPackages.find(
        (pkg) => pkg.value === this.formData.package
      );

      if (!foundPackage) {
        return "";
      }

      return foundPackage.label;
    },
    getCountryName() {
      return (
        this.supplierDetailInfo?.country.name ||
        this.supplierDetailInfo?.destination.name
      );
    },
  },
  mounted() {
    window.fetchSupplierInfo(this.resortId).then((response) => {
      const supplier = response.supplier;
      this.supplierDetailInfo = supplier;
      this.priceBasis = get(response, "supplier.room_rate_basis");

      this.supplier.type = supplier.type;
      this.supplier.availableDays = supplier.available_days.map((d) =>
        d.toString()
      );
      this.supplier.name = response.supplier.name;

      if (this.supplier.type === "package") {
        let packages = [];

        for (const pkg in response.number_of_nights) {
          packages.push({ value: pkg, label: response.number_of_nights[pkg] });
        }

        this.supplier.resortPackages = packages;

        if (moment(this.formData.date).isValid()) {
          const weekday = this.formData.date.weekday();
          if (
            this.supplier.availableDays.includes(weekday.toString()) === false
          ) {
            this.formData.date = null;
          }
        }
      }
    });
  },
  methods: {
    isMobile() {
      return window.matchMedia("only screen and (max-width: 1200px)").matches;
    },
    checkBeforeSubmit() {
      if (!this.formData.date && this.supplier.type === "package") {
        if (this.isMobile()) {
          this.openMobileBookingWidget(1);
        } else {
          this.$refs.ttDates.show();
        }

        return false;
      } else if (
        (!this.formData.dates[0] || !this.formData.dates[1]) &&
        this.supplier.type === "custom"
      ) {
        if (this.isMobile()) {
          this.openMobileBookingWidget(1);
        } else {
          this.$refs.ttDates.show();
        }

        return false;
      } else if (this.formData.guests === "0") {
        if (this.isMobile()) {
          this.openMobileBookingWidget(2);
        } else {
          this.$refs.ttGuests.show();
        }

        return false;
      } else if (
        this.supplier.type === "package" &&
        this.formData.package === "0"
      ) {
        if (this.isMobile()) {
          this.openMobileBookingWidget(3);
        } else {
          this.$refs.ttPackages.show();
        }

        return false;
      }

      return true;
    },
    handleSubmitEnquire() {
      const canSubmit = this.checkBeforeSubmit();
      const selectedCurrency = getSelectedCurrency();

      if (canSubmit) {
        const numGuests =
          this.formData.guests === "10+" ? "10" : this.formData.guests;

        const params = {
          "_zb-sid": window.Craft.session.sign || "",
          guest_num: numGuests,
          supplier_id: this.resortId,
          currency: selectedCurrency.code,
        };

        if (this.supplier.type === "custom") {
          params.from_date = this.formData.dates[0].format("YYYY-MM-DD");
          params.to_date = this.formData.dates[1].format("YYYY-MM-DD");
        } else if (this.supplier.type === "package") {
          params.from_date = this.formData.date.format("YYYY-MM-DD");
          params.num_nights = this.formData.package;
        }

        const qs = new URLSearchParams(params).toString();
        const path = this.supplier.type === "custom" ? "c" : "p";

        const url = `${window.ZUBLU_VARIABLES.BOOKING_APP}/${path}?${qs}`;
        window.open(url, "_blank");
      }
    },
    openMobileBookingWidget(step) {
      this.$refs.mobileBookingWidget.show(step);
    },
    handleShow() {
      toggleTrengoChat({ show: false });
    },
    handleHide() {
      toggleTrengoChat({ show: true });
    },
    handleChat(openChat) {
      toggleTrengoChat({ show: true });

      openChat();
    },
  },
};
</script>
